'use client';
import { Sidebar, TextArea } from 'components/common';
import {
  MintHrDropdown,
  MintHrDropdownItem,
} from 'components/common/MintHrDropdown';
import UserShortCut from 'components/common/UserShortCut';
import { useAuthContext } from 'contexts/AuthContext';
import { useDialog } from 'contexts/DialogContext';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import {
  DeleteDocumentService,
  ExtractHtmlFromTemplateService,
  GetInfoDemandeDocument,
  ProcessedDemandeDocument,
} from 'features/documents/services/document';
import { DocumentDemandInfo } from 'features/documents/types/document';
import { t } from 'i18next';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { appendToFormData } from 'utils/globalMethodes';
import { hasPermission } from 'utils/permissions';
import CreateDocument from '../CreateDocument';
import { Form, Formik } from 'formik';
import FileUpload from 'components/common/FileUpload';
import useFetchData from 'hooks/useFetch';
import moment from 'moment';
import { useMediaContext } from 'contexts/MediaContext';
import Tippy from '@tippyjs/react';
import EditTemplate from './EditTemplate';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import * as Yup from 'yup';

interface ParamsType {
  id: number;
  refetch?: () => void;
}
const InfoDemandeDocument: React.FC<SideBarProps<ParamsType>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { data: infoDemande } = useFetchData(GetInfoDemandeDocument, [
    params.id,
  ]);
  const imageAndPdfExt = ['jpg', 'jpeg', 'png', 'pdf'];
  const { user } = useAuthContext();
  const dialog = useDialog();
  const { closeSidebar, openSidebar } = useSidebarManager();
  const { openMedia } = useMediaContext();
  const [AcceptedLoading, setAcceptedLoading] = useState(false);
  const [RejectedLoading, setRejectedLoading] = useState(false);

  const handelDeleteDemande = (id: number): void => {
    dialog
      .delete('docdemand.delete_title', 'docdemand.delete_description')
      .then(async (res) => {
        if (res) {
          const response = await DeleteDocumentService(id);
          if (response.success) {
            closeSidebar(InfoDemandeDocument);
            toast.success(t('Default.succesMessage'));
            if (params.refetch) {
              params.refetch();
            }
          } else {
            toast.error(response.message);
          }
        }
      });
  };

  const handelStatusDemande = async (
    values: DocumentDemandInfo,
  ): Promise<void> => {
    try {
      if (values.processed === true) {
        setAcceptedLoading(true);
      } else {
        setRejectedLoading(true);
      }
      const response = await ProcessedDemandeDocument(appendToFormData(values));
      if (response.success) {
        dialog.close();
        if (params.refetch) {
          params.refetch();
        }
        closeSidebar(InfoDemandeDocument);
        toast.success(t('Default.succesMessage'));
      } else {
        toast.error(response.message);
      }
    } catch {
      toast.error(t('Default.errorMessage'));
    } finally {
      setAcceptedLoading(false);
      setRejectedLoading(false);
    }
  };

  const handelShowFile = (
    id: number | string,
    title: string,
    ext: 'pdf' | 'img',
  ): void => {
    openMedia(`/upload/document/${id}`, ext, title);
  };

  const handelShowTemplate = (
    id: number | string,
    title: string,
    ext: 'pdf' | 'img',
    withHeader: boolean,
  ): void => {
    openMedia(`/upload/downloadtemplatedemand/${id}/${withHeader}`, ext, title);
  };

  const handelEditTemplate = async (
    id: number,
    typeId: number,
  ): Promise<void> => {
    const response = await ExtractHtmlFromTemplateService(id, typeId);
    if (response.success) {
      openSidebar(EditTemplate, true, {
        id,
        text: response.data.html,
        refetch: params.refetch ?? (() => {}),
      });
    } else {
      toast.error(response.message);
    }
  };

  const isPDF = (filename: string): boolean => {
    const parts = filename.split('.');
    const extension = parts.pop();
    return extension?.toLowerCase() === 'pdf';
  };
  const validationSchema = Yup.object({
    raison: Yup.string().trim().required(t('RequiredAttribute')),
  });

  const handleReject = (
    setFieldValue: (
      field: string,
      value: boolean | string,
      shouldValidate?: boolean,
    ) => void,
    submitForm: () => Promise<void>,
  ): void => {
    const handelSubmitReject = (values: { raison: string }): void => {
      setFieldValue('processed', false);
      setFieldValue('raison', values.raison);
      submitForm();
    };

    dialog.custom(
      <Formik
        validationSchema={validationSchema}
        initialValues={{ raison: '' }}
        onSubmit={handelSubmitReject}
      >
        <Form
          className="minthr-box-shadow minthr-border-rounded  minthr-bg-white minthr-p-3 show "
          style={{
            width: 'calc(var(--unit-minthr)*400)',
          }}
        >
          <div className="minthr-mb-4 minthr-flex minthr-flex-items-center w-100">
            <h4 className="minthr-mb-2">{t('Raison')}</h4>
            <button
              className="minthr-sidebar-close minthr-ml-auto"
              type="button"
              onClick={() => {
                dialog.close();
              }}
            >
              <img
                className="minthr-icon minthr-icon-gray"
                alt="Close"
                src="/icons/close.svg"
              />
            </button>
          </div>
          <TextArea label={t('Raison')} name="raison" />

          <button
            type="submit"
            className="minthr-btn minthr-btn-danger minthr-ml-auto minthr-mt-4"
          >
            {t('btn.reject')}
          </button>
        </Form>
      </Formik>,
    );
  };

  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      {infoDemande && (
        <>
          <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-3 minthr-border-b minthr-border-b-3">
            <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
              <img
                className="minthr-icon-m"
                src="/icons/stopwatch.svg"
                alt=""
              />
            </div>
            <h3>{t('DocDemand.InfoModal.Title')}</h3>

            <button
              onClick={() => {
                closeSidebar(InfoDemandeDocument);
              }}
              className="minthr-sidebar-close"
              type="button"
            >
              <img src="/icons/close.svg" alt="" />
            </button>
          </div>

          <Formik
            initialValues={{ ...infoDemande, files: [] }}
            onSubmit={(values) => {
              handelStatusDemande(values);
            }}
          >
            {({ setFieldValue, values, submitForm }) => (
              <Form className="w-100">
                <div className="minthr-flex minthr-flex-items-center minthr-mb-3">
                  <h5 className="minthr-color-primary">
                    {t('Demande.Info.EditDemande.Title')}
                  </h5>

                  {infoDemande?.status == 0 && (
                    <>
                      {(user.userId === infoDemande.userid ||
                        hasPermission('DemandDocument.Manage')) && (
                        <button
                          onClick={() => {
                            openSidebar(CreateDocument, true, {
                              id: infoDemande.id,
                              refetch: params.refetch,
                            });
                          }}
                          type="button"
                          className="minthr-btn minthr-btn-s minthr-btn-icon-origin minthr-ml-auto minthr-p-0"
                        >
                          <img src="/icons/edit.svg" alt="Edit" />
                        </button>
                      )}

                      {hasPermission('DemandDocument.Manage') && (
                        <button
                          onClick={() => {
                            handelDeleteDemande(infoDemande.id);
                          }}
                          type="button"
                          className="minthr-btn minthr-btn-s minthr-btn-icon-origin minthr-icon-danger minthr-px-1"
                        >
                          <img src="/icons/delete.svg" alt="Delete" />
                        </button>
                      )}
                    </>
                  )}
                </div>

                <div className="minthr-pt-0">
                  <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                    <div className="col-sm-4">
                      <span className="minthr-text-uppercase p">
                        {t('AskedBy')}
                      </span>
                    </div>
                    <div className="col-sm-8">
                      {infoDemande.userid &&
                      infoDemande.userFullName !== ' ' ? (
                        <div className="minthr-flex minthr-flex-items-center minthr-color-dark h5">
                          <h5>{infoDemande.userFullName}</h5>
                          <UserShortCut
                            src={`upload/profileimages/${infoDemande?.userid}/${infoDemande?.pathImg}`}
                            alt=""
                            className="minthr-avatar minthr-avatar-s minthr-ml-2"
                          />
                        </div>
                      ) : (
                        <div className="minthr-flex minthr-flex-items-center minthr-color-dark h5">
                          <h5 style={{ color: '#f26c6d' }}>
                            {t('User.notFound')}
                          </h5>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                    <div className="col-sm-4">
                      <span className="minthr-text-uppercase p">
                        {t('Statut')}
                      </span>
                    </div>
                    <div className="col-sm-8">
                      <div
                        className={`minthr-badge minthr-badge-${infoDemande.status == 0 ? 'warning' : infoDemande.status == 1 ? 'success' : 'danger'} minthr-badge-s minthr-px-2`}
                      >
                        {t(
                          infoDemande.status == 0
                            ? 'Statut.OnHold'
                            : infoDemande.status == 1
                              ? 'Status.Accepter'
                              : 'Status.Refuser',
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                    <div className="col-sm-4">
                      <span className="minthr-text-uppercase p">
                        {t('date')}
                      </span>
                    </div>
                    <div className="col-sm-8">
                      <div className="minthr-color-dark h5">
                        {moment(infoDemande.createdDate).format('L')}
                      </div>
                    </div>
                  </div>
                  <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                    <div className="col-sm-4">
                      <span className="minthr-text-uppercase p">
                        {t('Type')}
                      </span>
                    </div>
                    <div className="col-sm-8">
                      <div className="minthr-color-dark h5">
                        {infoDemande.type}
                      </div>
                    </div>
                  </div>
                  <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                    <div className="col-sm-4">
                      <span className="minthr-text-uppercase p">
                        {t('Version')}
                      </span>
                    </div>
                    <div className="col-sm-8">
                      <div className="minthr-color-dark h5">
                        {infoDemande.version == 1 || infoDemande.version == 0
                          ? 'Digitale'
                          : 'Papier'}
                      </div>
                    </div>
                  </div>
                  {infoDemande.projectName && (
                    <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                      <div className="col-sm-4">
                        <span className="minthr-text-uppercase p">
                          {t('ProjectName')}
                        </span>
                      </div>
                      <div className="col-sm-8">
                        <div className="minthr-color-dark h5">
                          {infoDemande.projectName}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                    <div className="col-sm-12 minthr-mb-2">
                      <span className="minthr-text-uppercase p">
                        {t('Description')}
                      </span>
                    </div>
                    <div className="col-sm-12">
                      <div className="minthr-color-dark h5">
                        {infoDemande.remark}
                      </div>
                    </div>
                  </div>
                  <div className="row g-0 minthr-my-2 ">
                    {infoDemande.filesNames != null &&
                      infoDemande.filesNames
                        .filter((x) => !x.doc)
                        .map((item, index) => (
                          <div key={index} className="col-sm-12 ">
                            <a className="minthr-flex minthr-flex-items-center minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3 minthr-h-vh-25">
                              <img
                                className="minthr-icon-xl minthr-mr-2"
                                src="/icons/doc-pdf.svg"
                                alt=""
                              />
                              <div className="minthr-mr-6 minthr-pr-3">
                                <h6>{item.name}</h6>
                              </div>
                              <button className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 download-file">
                                <img
                                  className="minthr-icon-s minthr-icon-gray"
                                  src="/icons/download.svg"
                                  alt=""
                                />
                              </button>
                              {infoDemande.status == 0 && (
                                <button className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-ml-auto minthr-mr-2 file-remove">
                                  <img
                                    className="minthr-icon-s minthr-icon-danger"
                                    src="/icons/delete.svg"
                                    alt=""
                                  />
                                </button>
                              )}
                            </a>
                          </div>
                        ))}
                  </div>

                  {(hasPermission('DemandDocument.Manage') ||
                    infoDemande.userid == user.userId) && (
                    <>
                      <div className="minthr-flex minthr-flex-items-center minthr-mt-3 minthr-mb-3 minthr-pt-6 minthr-border-t">
                        <h4>{t('DocumentDemand.Traitement')}</h4>
                      </div>

                      <div className="row g-0 row-cols-sm-3 minthr-mt-3 minthr-mb-6">
                        {hasPermission('DemandDocument.Manage') &&
                          infoDemande.template &&
                          infoDemande.status === 0 &&
                          !values.deleteTemplate && (
                            <div className="col-sm-4 minthr-px-1 minthr-mb-2">
                              <div className="minthr-box minthr-box-shadow minthr-box-round minthr-p-3 minthr-pb-2 minthr-h-vh-25">
                                <div className="minthr-flex minthr-mb-2">
                                  <div className="minthr-icon-bg minthr-icon-bg-l minthr-bg-secondary minthr-icon-rounded">
                                    <img
                                      className="minthr-icon-m minthr-mobile-icon-s"
                                      src="/icons/accounting.svg"
                                      alt=""
                                    />
                                  </div>
                                  <Tippy content={t('template.tooltiptitle')}>
                                    <img
                                      className="minthr-icon-m minthr-mobile-icon-s minthr-icon-toxic minthr-ml-auto"
                                      src="/icons/info.svg"
                                      alt=""
                                    />
                                  </Tippy>
                                </div>
                                <div className="h5 minthr-border-b minthr-pb-1 minthr-mb-3 minthr-min-h-70 minthr-break-word minthr-text-3-lines">
                                  {infoDemande.type}
                                </div>
                                <div className="minthr-flex inthr-flex-items-center minthr-flex-space-between">
                                  <a
                                    onClick={() => {
                                      handelShowTemplate(
                                        infoDemande.id,
                                        infoDemande.type,
                                        'pdf',
                                        true,
                                      );
                                    }}
                                    className="minthr-icon-text minthr-color-toxic minthr-template-doc"
                                  >
                                    {t('Visualiser')}
                                  </a>
                                  <MintHrDropdown
                                    title={
                                      <div className="dropdown-toggle minthr-ml-auto cursor-pointer">
                                        <img
                                          className="minthr-icon-s minthr-icon-gray"
                                          src="/icons/options-dots.svg"
                                          alt=""
                                        />
                                      </div>
                                    }
                                  >
                                    <MintHrDropdownItem
                                      onClick={() => {
                                        handelEditTemplate(
                                          infoDemande.id,
                                          infoDemande.typeId,
                                        );
                                      }}
                                    >
                                      {t('doc.edit')}
                                    </MintHrDropdownItem>
                                    <MintHrDropdownItem
                                      onClick={() => {
                                        handelShowTemplate(
                                          infoDemande.id,
                                          infoDemande.type,
                                          'pdf',
                                          false,
                                        );
                                      }}
                                    >
                                      {t('doc.download.Without.Headers')}
                                    </MintHrDropdownItem>

                                    <MintHrDropdownItem
                                      onClick={() => {
                                        setFieldValue('deleteTemplate', true);
                                      }}
                                    >
                                      {t('delete')}
                                    </MintHrDropdownItem>
                                  </MintHrDropdown>
                                </div>
                              </div>
                            </div>
                          )}

                        {infoDemande.filesNames !== null &&
                          infoDemande.filesNames
                            .filter((x) => x.doc)
                            .map((item, index) => {
                              const fileNameParts = item.name.split('.');
                              const fileExtension =
                                fileNameParts[fileNameParts.length - 1];
                              const isImageOrPdf =
                                imageAndPdfExt.includes(fileExtension);

                              return (
                                <div
                                  className="col-sm-4 minthr-px-1 minthr-mb-2"
                                  key={index}
                                >
                                  <div className="minthr-box minthr-box-shadow minthr-box-round minthr-p-3 minthr-pb-2">
                                    <div className="minthr-flex minthr-mb-2">
                                      <div className="minthr-icon-bg minthr-icon-bg-l minthr-bg-secondary minthr-icon-rounded">
                                        <img
                                          className="minthr-icon-m minthr-mobile-icon-s"
                                          src="/icons/accounting.svg"
                                          alt=""
                                        />
                                      </div>
                                    </div>
                                    <div className="h5 minthr-border-b minthr-pb-1 minthr-min-h-70 minthr-break-word minthr-mb-3 minthr-text-3-lines">
                                      {item.name}
                                    </div>
                                    <div className="minthr-flex inthr-flex-items-center minthr-flex-space-between ">
                                      {isImageOrPdf ? (
                                        <a
                                          onClick={() => {
                                            handelShowFile(
                                              item.path,
                                              infoDemande.type,
                                              isPDF(item.path) ? 'pdf' : 'img',
                                            );
                                          }}
                                          className="minthr-icon-text minthr-color-toxic minthr-img-view"
                                        >
                                          {t('Visualiser')}
                                        </a>
                                      ) : (
                                        <a
                                          href={`/upload/document/${item.path}`}
                                          download
                                          title={item.name}
                                          className="minthr-icon-text minthr-color-toxic download-file-doc"
                                        >
                                          {t('download')}
                                        </a>
                                      )}
                                      {infoDemande.status === 0 && (
                                        <a
                                          className="file-remove-doc"
                                          type="button"
                                        >
                                          <img
                                            className="minthr-icon-s minthr-icon-danger"
                                            src="/icons/delete.svg"
                                            alt=""
                                          />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                        {infoDemande.status == 0 &&
                          hasPermission('DemandDocument.Manage') && (
                            <FileUpload
                              name="files"
                              allowedFileTypes="*"
                              initialFiles={values.filesNames}
                              rows={false}
                              useCustomCard={true}
                              inputContainerClassName="col-sm-4 minthr-px-1 minthr-mb-2 minthr-flex-order2"
                              customCardRenderer={(fileInfo, onRemove) => (
                                <div
                                  className="col-sm-4 minthr-px-1 minthr-mb-2"
                                  key={fileInfo.index}
                                >
                                  <div className="minthr-box minthr-box-shadow minthr-box-round minthr-p-3 minthr-pb-2 minthr-h-vh-25">
                                    <div className="minthr-flex minthr-mb-2">
                                      <div className="minthr-icon-bg minthr-icon-bg-l minthr-bg-secondary minthr-icon-rounded">
                                        <img
                                          className="minthr-icon-m minthr-mobile-icon-s"
                                          src="/icons/accounting.svg"
                                          alt=""
                                        />
                                      </div>
                                      {fileInfo.isUploading && (
                                        <div className="minthr-ml-auto">
                                          <img
                                            className="minthr-loading-spinner minthr-icon-s"
                                            src="/icons/spinner-outer.svg"
                                            alt="Loading"
                                          />
                                        </div>
                                      )}
                                    </div>
                                    <div className="h5 minthr-border-b minthr-pb-1 minthr-min-h-70 minthr-break-word minthr-mb-3 minthr-text-3-lines">
                                      {fileInfo.name}
                                    </div>
                                    <div className="minthr-flex inthr-flex-items-center minthr-flex-space-between align-items-center">
                                      <span className="text-s minthr-color-gray">
                                        {fileInfo.size}
                                      </span>
                                      {!fileInfo.isUploading && (
                                        <button
                                          onClick={onRemove}
                                          type="button"
                                          className="minthr-p-0 minthr-border-none minthr-bg-transparent"
                                        >
                                          <img
                                            className="minthr-icon-s minthr-icon-danger"
                                            src="/icons/delete.svg"
                                            alt="Delete"
                                          />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            />
                          )}
                      </div>
                    </>
                  )}
                </div>
                {infoDemande.status == 0 &&
                  (hasPermission('DemandDocument.Manage') ||
                    infoDemande.isApprover) && (
                    <div className="row minthr-sidebar-footer minthr-box-shadow minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-py-4 minthr-mx-n5 minthr-px-4 minthr-mt-auto">
                      <button
                        onClick={() => {
                          handleReject(setFieldValue, submitForm);
                          // setFieldValue('processed', false);
                        }}
                        type="button"
                        className="col minthr-hover-danger minthr-btn minthr-btn-outline minthr-btn-danger "
                        disabled={RejectedLoading || AcceptedLoading}
                      >
                        {RejectedLoading ? <LoadingIndicator /> : t('Rejected')}
                      </button>
                      <button
                        onClick={() => {
                          setFieldValue('processed', true);
                        }}
                        type="submit"
                        className="col minthr-btn minthr-btn-primary minthr-ml-2"
                        disabled={RejectedLoading || AcceptedLoading}
                      >
                        {AcceptedLoading ? <LoadingIndicator /> : t('Accepted')}
                      </button>
                    </div>
                  )}
              </Form>
            )}
          </Formik>
        </>
      )}
    </Sidebar>
  );
};

export default InfoDemandeDocument;
